import { useMemo, useState } from 'react';
import { format, eachDayOfInterval, subDays, parseISO, startOfDay, getHours } from 'date-fns';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';

interface PageView {
  created_at: string;
}

interface Props {
  pageViews: PageView[];
  days: number;
}

type DrillState =
  | { level: 'overview' }
  | { level: 'day'; date: string; label: string };

const ORANGE = '#f97316';
const ORANGE_LIGHT = '#fed7aa';

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl pointer-events-none">
      <p className="font-semibold mb-0.5">{label}</p>
      <p className="text-orange-400">{payload[0].value.toLocaleString()} views</p>
    </div>
  );
}

export default function PageViewsChart({ pageViews, days }: Props) {
  const [drill, setDrill] = useState<DrillState>({ level: 'overview' });
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const overviewData = useMemo(() => {
    const end = new Date();
    const start = subDays(end, days - 1);
    return eachDayOfInterval({ start, end }).map(day => {
      const dayStr = format(day, 'yyyy-MM-dd');
      const count = pageViews.filter(pv => {
        try {
          const d = new Date(pv.created_at);
          const local = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          return local === dayStr;
        } catch {
          return false;
        }
      }).length;
      return {
        date: dayStr,
        label: format(day, days <= 7 ? 'EEE' : 'MMM d'),
        count,
      };
    });
  }, [pageViews, days]);

  const dayData = useMemo(() => {
    if (drill.level !== 'day') return [];
    const dayViews = pageViews.filter(pv => {
      try {
        const d = new Date(pv.created_at);
        const local = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        return local === drill.date;
      } catch {
        return false;
      }
    });
    return Array.from({ length: 24 }, (_, h) => ({
      hour: h,
      label: format(new Date(2000, 0, 1, h), 'ha'),
      count: dayViews.filter(pv => {
        try { return getHours(parseISO(pv.created_at)) === h; } catch { return false; }
      }).length,
    }));
  }, [pageViews, drill]);

  const total = overviewData.reduce((s, d) => s + d.count, 0);
  const peak = Math.max(...overviewData.map(d => d.count), 0);
  const avg = overviewData.length ? (total / overviewData.length).toFixed(1) : '0';

  const dayTotal = dayData.reduce((s, d) => s + d.count, 0);
  const dayPeak = Math.max(...dayData.map(d => d.count), 0);

  const handleBarClick = (data: any) => {
    if (!data?.activePayload?.[0]) return;
    const point = data.activePayload[0].payload;
    setDrill({ level: 'day', date: point.date, label: point.label });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-2">
          {drill.level === 'day' && (
            <button
              onClick={() => setDrill({ level: 'overview' })}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 transition-colors mr-1"
            >
              <ChevronLeftIcon className="w-3.5 h-3.5" />
              Back
            </button>
          )}
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {drill.level === 'overview'
                ? 'Page Views Over Time'
                : `Page Views — ${drill.label}`}
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {drill.level === 'overview'
                ? <>Last {days} days &middot; <span className="text-orange-500 cursor-pointer">click a point to drill in</span></>
                : 'Hourly breakdown'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-6">
          {drill.level === 'overview' ? (
            <>
              <div className="text-right">
                <p className="text-xs text-gray-400 mb-0.5">Total</p>
                <p className="text-lg font-bold text-gray-900 leading-none">{total.toLocaleString()}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400 mb-0.5">Peak day</p>
                <p className="text-lg font-bold text-orange-500 leading-none">{peak.toLocaleString()}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400 mb-0.5">Avg / day</p>
                <p className="text-lg font-bold text-gray-900 leading-none">{avg}</p>
              </div>
            </>
          ) : (
            <>
              <div className="text-right">
                <p className="text-xs text-gray-400 mb-0.5">Day total</p>
                <p className="text-lg font-bold text-gray-900 leading-none">{dayTotal.toLocaleString()}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400 mb-0.5">Peak hour</p>
                <p className="text-lg font-bold text-orange-500 leading-none">{dayPeak.toLocaleString()}</p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="h-52">
        {drill.level === 'overview' ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={overviewData}
              margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
              onClick={handleBarClick}
              className="cursor-pointer"
            >
              <defs>
                <linearGradient id="pgv-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={ORANGE} stopOpacity={0.15} />
                  <stop offset="95%" stopColor={ORANGE} stopOpacity={0.01} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
                width={36}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ stroke: ORANGE, strokeWidth: 1, strokeDasharray: '4 4' }} />
              <Area
                type="monotone"
                dataKey="count"
                stroke={ORANGE}
                strokeWidth={2}
                fill="url(#pgv-grad)"
                dot={(props: any) => {
                  const { cx, cy, index } = props;
                  if (props.value === 0) return <g key={index} />;
                  return (
                    <circle
                      key={index}
                      cx={cx} cy={cy} r={3}
                      fill="white"
                      stroke={ORANGE}
                      strokeWidth={2}
                    />
                  );
                }}
                activeDot={{ r: 5, fill: ORANGE, stroke: 'white', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={dayData}
              margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
              barCategoryGap="20%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 9, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
                interval={1}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={false}
                width={32}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f9fafb' }} />
              <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                {dayData.map((entry, index) => (
                  <Cell
                    key={index}
                    fill={entry.count === dayPeak && dayPeak > 0 ? ORANGE : ORANGE_LIGHT}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
